/**
 * Renders Slack mrkdwn segments: emphasis, code, lists, mentions, channels, links.
 */

import React from 'react';
import type { SlackNameMap, SlackTextSegment } from '../../domain/slackMrkdwn';
import { slackTextToSegments } from '../../domain/slackMrkdwn';
import { getExternalLinkConfig } from '../../config/externalLinks';
import './slackRichText.css';

export interface SlackRichTextProps {
  text: string;
  names?: SlackNameMap;
  className?: string;
  style?: React.CSSProperties;
  /** Use a semantic element other than <span> (e.g. inherit from parent). */
  as?: 'span' | 'p' | 'div';
}

function renderSegment(segment: SlackTextSegment, index: number): React.ReactNode {
  switch (segment.type) {
    case 'text':
      return <React.Fragment key={index}>{segment.value}</React.Fragment>;
    case 'br':
      return <br key={index} className="z-srt-br" />;
    case 'bold':
      return (
        <strong key={index} className="z-srt-bold">
          {segment.children.map(renderSegment)}
        </strong>
      );
    case 'italic':
      return (
        <em key={index} className="z-srt-italic">
          {segment.children.map(renderSegment)}
        </em>
      );
    case 'strike':
      return (
        <s key={index} className="z-srt-strike">
          {segment.children.map(renderSegment)}
        </s>
      );
    case 'code':
      return (
        <code key={index} className="z-srt-code">
          {segment.value}
        </code>
      );
    case 'codeBlock':
      return (
        <pre key={index} className="z-srt-codeblock">
          <code>{segment.value}</code>
        </pre>
      );
    case 'list': {
      const ListTag = segment.ordered ? 'ol' : 'ul';
      return (
        <ListTag key={index} className={`z-srt-list ${segment.ordered ? 'z-srt-list--ordered' : 'z-srt-list--bullet'}`}>
          {segment.items.map((item, itemIdx) => (
            <li key={itemIdx} className="z-srt-list-item">
              {item.map(renderSegment)}
            </li>
          ))}
        </ListTag>
      );
    }
    case 'mention':
      return (
        <span key={index} className="z-srt-mention">
          @{segment.label}
        </span>
      );
    case 'channel':
      return (
        <span key={index} className="z-srt-channel">
          {segment.label}
        </span>
      );
    case 'link':
      return (
        <a
          key={index}
          className="z-srt-link"
          href={segment.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => event.stopPropagation()}
        >
          {segment.label}
        </a>
      );
    default: {
      const _exhaustive: never = segment;
      return _exhaustive;
    }
  }
}

export const SlackRichText: React.FC<SlackRichTextProps> = ({
  text,
  names = {},
  className,
  style,
  as = 'span',
}) => {
  const segments = slackTextToSegments(text, names, getExternalLinkConfig());
  const Tag = as;
  const classes = ['z-srt', className].filter(Boolean).join(' ');
  return (
    <Tag className={classes} style={style}>
      {segments.map(renderSegment)}
    </Tag>
  );
};
